<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>

    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <h1 class="fw-bold page-title">설비 알림톡 추적</h1>
          </div>
          <span class="pageDesc">
            보증번호별로 작성부터 승인완료까지 알림톡 발송 시도와 결과를 스텝 단위로 확인합니다.
          </span>
        </div>
      </div>
    </div>

    <!-- 콘텐츠 영역 -->
    <div class="card-body px-0 pt-3 pb-2">
      <div class="card content-wrap-card">
        <!-- 검색 필터 -->
        <div class="filter-bar">
          <a-form layout="inline">
            <a-form-item label="보증번호">
              <a-input
                v-model:value="filters.warrantyNo"
                placeholder="부분 검색"
                allow-clear
                style="width: 180px"
                @press-enter="handleSearch"
              />
            </a-form-item>

            <a-form-item label="회원사명">
              <a-input
                v-model:value="filters.mmbsNm"
                placeholder="부분 검색"
                allow-clear
                style="width: 160px"
                @press-enter="handleSearch"
              />
            </a-form-item>

            <a-form-item label="조합원번호">
              <a-input
                v-model:value="filters.mmbsNo"
                placeholder="정확히 일치"
                allow-clear
                style="width: 130px"
                @press-enter="handleSearch"
              />
            </a-form-item>

            <a-form-item label="스텝">
              <a-select
                v-model:value="filters.step"
                :options="stepOptions"
                style="width: 160px"
              />
            </a-form-item>

            <a-form-item label="스텝 상태">
              <a-select
                v-model:value="filters.stepStatus"
                :options="stepStatusOptions"
                style="width: 130px"
              />
            </a-form-item>

            <a-form-item>
              <a-checkbox v-model:checked="filters.onlyIssue">
                이슈 있는 건만
              </a-checkbox>
            </a-form-item>

            <a-form-item>
              <a-space>
                <a-button type="primary" @click="handleSearch">조회</a-button>
                <a-button @click="handleReset">초기화</a-button>
              </a-space>
            </a-form-item>
          </a-form>
        </div>

        <!-- 범례 -->
        <div class="legend">
          <span class="legend-item">
            <i class="legend-dot legend-dot--done"></i>발송 완료
          </span>
          <span class="legend-item">
            <i class="legend-dot legend-dot--in_flight"></i>처리 중
          </span>
          <span class="legend-item">
            <i class="legend-dot legend-dot--stalled"></i>컨슈머 미수신 (mq 리시버 문제)
          </span>
          <span class="legend-item">
            <i class="legend-dot legend-dot--failed"></i>실패 (읽었으나 처리·발송 실패)
          </span>
          <span class="legend-item">
            <i class="legend-dot legend-dot--missing"></i>트리거 없음 (조합 미호출 의심)
          </span>
          <span class="legend-item">
            <i class="legend-dot legend-dot--pending"></i>대기
          </span>
          <span class="legend-note">
            스텝 상태는 마지막 동기화 기준입니다. 상세를 열면 조합 상태를 다시 읽어옵니다.
          </span>
        </div>

        <!-- 목록 -->
        <a-table
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          :pagination="pagination"
          row-key="warrantyNo"
          size="middle"
          :scroll="{ x: 1200 }"
          @change="handleTableChange"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'warrantyNo'">
              <a @click="openDetail(record.warrantyNo)">{{ record.warrantyNo }}</a>
              <a-tag v-if="record.hasIssue" color="orange" class="issue-tag">
                확인 필요
              </a-tag>
            </template>

            <template v-else-if="column.key === 'steps'">
              <SeolbiStepTimeline :steps="record.steps" compact />
            </template>

            <template v-else-if="column.key === 'currentStep'">
              {{ labelOfStep(record.currentStep) }}
            </template>

            <template v-else-if="column.key === 'updatedAt'">
              {{ formatDate(record.updatedAt) }}
            </template>

            <template v-else-if="column.key === 'action'">
              <a-button size="small" @click="openDetail(record.warrantyNo)">
                상세
              </a-button>
            </template>
          </template>
        </a-table>
      </div>
    </div>

    <SeolbiTrackingDetailDrawer
      :open="drawerOpen"
      :loading="detailLoading"
      :detail="detail"
      @close="drawerOpen = false"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from "vue";
import { message } from "ant-design-vue";
import SeolbiStepTimeline from "./components/SeolbiStepTimeline.vue";
import SeolbiTrackingDetailDrawer from "./components/SeolbiTrackingDetailDrawer.vue";
import {
  seolbiTrackingApi,
  STEP_FILTER_OPTIONS,
  STEP_STATUS_FILTER_OPTIONS,
} from "./seolbiTrackingApi";

const columns = [
  {
    title: "보증번호",
    key: "warrantyNo",
    dataIndex: "warrantyNo",
    width: 200,
    fixed: "left",
  },
  { title: "회원사", key: "mmbsNm", dataIndex: "mmbsNm", width: 140 },
  { title: "계약건명", key: "ctrtThngNm", dataIndex: "ctrtThngNm", width: 200, ellipsis: true },
  { title: "수신번호", key: "phoneNumber", dataIndex: "phoneNumber", width: 120 },
  { title: "승인상태", key: "aprvStatus", dataIndex: "aprvStatus", width: 90 },
  { title: "진행 스텝", key: "steps", width: 300 },
  { title: "현재 스텝", key: "currentStep", width: 130 },
  { title: "갱신", key: "updatedAt", width: 150 },
  { title: "", key: "action", width: 80, fixed: "right" },
];

export default {
  name: "SeolbiAlimtalkTracking",
  components: { SeolbiStepTimeline, SeolbiTrackingDetailDrawer },
  setup() {
    const loading = ref(false);
    const detailLoading = ref(false);
    const rows = ref([]);
    const total = ref(0);
    const currentPage = ref(1);
    const pageSize = ref(20);

    const drawerOpen = ref(false);
    const detail = ref(null);

    const filters = reactive({
      warrantyNo: "",
      mmbsNm: "",
      mmbsNo: "",
      step: "",
      stepStatus: "",
      onlyIssue: false,
    });

    const pagination = computed(() => ({
      current: currentPage.value,
      pageSize: pageSize.value,
      total: total.value,
      showSizeChanger: true,
      showTotal: (count) => `총 ${count}건`,
    }));

    const buildParams = () => {
      const params = {
        page: currentPage.value,
        limit: pageSize.value,
      };

      if (filters.warrantyNo) params.warrantyNo = filters.warrantyNo;
      if (filters.mmbsNm) params.mmbsNm = filters.mmbsNm;
      if (filters.mmbsNo) params.mmbsNo = filters.mmbsNo;
      if (filters.onlyIssue) params.onlyIssue = "true";

      // 스텝 상태 필터는 대상 스텝이 함께 지정돼야 의미가 있습니다.
      if (filters.step && filters.stepStatus) {
        params.step = filters.step;
        params.stepStatus = filters.stepStatus;
      }

      return params;
    };

    const fetchList = async () => {
      loading.value = true;

      try {
        const response = await seolbiTrackingApi.getList(buildParams());

        rows.value = response.data?.rows ?? [];
        total.value = response.data?.total ?? 0;
      } catch (error) {
        message.error(
          error.response?.data?.message || "추적 목록을 불러오지 못했습니다.",
        );
        rows.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      if (filters.stepStatus && !filters.step) {
        message.warning("스텝 상태로 필터하려면 대상 스텝을 함께 선택하세요.");
        return;
      }

      currentPage.value = 1;
      fetchList();
    };

    const handleReset = () => {
      filters.warrantyNo = "";
      filters.mmbsNm = "";
      filters.mmbsNo = "";
      filters.step = "";
      filters.stepStatus = "";
      filters.onlyIssue = false;
      currentPage.value = 1;
      fetchList();
    };

    const handleTableChange = (page) => {
      currentPage.value = page.current;
      pageSize.value = page.pageSize;
      fetchList();
    };

    const openDetail = async (warrantyNo) => {
      drawerOpen.value = true;
      detailLoading.value = true;
      detail.value = null;

      try {
        // 상세 조회는 서버에서 조합 상태를 동기화하므로 목록보다 최신입니다.
        const response = await seolbiTrackingApi.getDetail(warrantyNo);
        detail.value = response.data;

        // 동기화 결과를 목록에도 반영합니다.
        const index = rows.value.findIndex((row) => row.warrantyNo === warrantyNo);
        if (index >= 0) {
          rows.value[index] = { ...rows.value[index], ...response.data };
        }
      } catch (error) {
        message.error(
          error.response?.data?.message || "추적 상세를 불러오지 못했습니다.",
        );
        drawerOpen.value = false;
      } finally {
        detailLoading.value = false;
      }
    };

    const labelOfStep = (step) => {
      if (!step) return "-";

      return (
        STEP_FILTER_OPTIONS.find((option) => option.value === step)?.label ?? step
      );
    };

    const formatDate = (value) =>
      value ? new Date(value).toLocaleString("ko-KR") : "-";

    onMounted(fetchList);

    return {
      columns,
      loading,
      detailLoading,
      rows,
      pagination,
      filters,
      drawerOpen,
      detail,
      stepOptions: STEP_FILTER_OPTIONS,
      stepStatusOptions: STEP_STATUS_FILTER_OPTIONS,
      handleSearch,
      handleReset,
      handleTableChange,
      openDetail,
      labelOfStep,
      formatDate,
    };
  },
};
</script>

<style scoped>
.filter-bar {
  padding: 16px 16px 0;
}

.legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 12px 16px;
  font-size: 12px;
  color: #595959;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1px solid #d9d9d9;
}

.legend-dot--done {
  background: #52c41a;
  border-color: #52c41a;
}

.legend-dot--in_flight {
  background: #1890ff;
  border-color: #1890ff;
}

.legend-dot--stalled {
  background: #eb2f96;
  border-color: #eb2f96;
}

.legend-dot--failed {
  background: #f5222d;
  border-color: #f5222d;
}

.legend-dot--missing {
  background: #fa8c16;
  border-color: #fa8c16;
}

.legend-dot--pending {
  background: #fafafa;
}

.legend-note {
  margin-left: auto;
  color: #8c8c8c;
}

.issue-tag {
  margin-left: 6px;
}
</style>
